import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { downloadFichaTecnicaInsumosPdf } from '../../lib/exportFichaTecnicaInsumosPdf'
import type { FichaInsumosExportPayload } from '../../lib/exportFichaTecnicaInsumosXlsx'
import {
  downloadFichasTecnicasXlsx,
  type FichaExportPayload,
} from '../../lib/exportFichaTecnicaXlsx'
import {
  buildFichaInsumosPayload,
  filterFichaItensPorPrato,
} from '../../lib/fichaCatalogoExport'
import {
  labelRefeicao,
  nutrientesDaPorcao,
  refeicoesDoTurno,
  somarNutrientes,
  type RefeicaoFicha,
} from '../../lib/fichaTecnicaPacote'
import { formatNumber, todayISO } from '../../lib/utils'
import { supabase } from '../../lib/supabase'
import type {
  AlimentoMacronutriente,
  FichaTecnica,
  FichaTecnicaItem,
  FichaTecnicaPrato,
  ModalidadeEnsino,
  Preco,
  Produto,
  Prato,
  TurnoReferencia,
} from '../../types/database'
import { REFEICOES, TURNOS_REFERENCIA } from '../../types/database'

type GrupoFiltro = 'todas' | 'infantil' | 'fund1' | 'fund2' | 'eja'

const GRUPOS_FILTRO: { id: GrupoFiltro; label: string; modalidades: ModalidadeEnsino[] }[] = [
  { id: 'todas', label: 'Todas as modalidades', modalidades: [] },
  { id: 'infantil', label: 'Infantil', modalidades: ['pre'] },
  { id: 'fund1', label: 'Fundamental Menor', modalidades: ['fund1'] },
  { id: 'fund2', label: 'Fundamental Maior', modalidades: ['fund2'] },
  { id: 'eja', label: 'EJA', modalidades: ['eja'] },
]

const MOD_LABEL: Partial<Record<ModalidadeEnsino, string>> = {
  creche: 'Infantil',
  pre: 'Infantil',
  fund1: 'Fundamental Menor',
  fund2: 'Fundamental Maior',
  eja: 'EJA',
  medio: 'Médio',
  integral: 'Integral',
}

export type CatalogoAjustarTarget = {
  grupoId: 'infantil' | 'fund1' | 'fund2' | 'eja'
  turno: TurnoReferencia
  refeicao: RefeicaoFicha
}

type Row = {
  ficha: FichaTecnica
  pratoId: string | null
  pratoNome: string
  modoPreparoLabel: string
  itensLabel: string
  kcal: number
  ptn: number
  cho: number
  lip: number
  exportPayload: FichaExportPayload
  insumosPayload: FichaInsumosExportPayload
}

type Props = {
  onError: (msg: string | null) => void
  onInfo: (msg: string | null) => void
  onAjustar: (target: CatalogoAjustarTarget) => void
  onNova: () => void
}

function grupoFromModalidade(mod: ModalidadeEnsino): CatalogoAjustarTarget['grupoId'] {
  if (mod === 'creche' || mod === 'pre') return 'infantil'
  if (mod === 'fund2') return 'fund2'
  if (mod === 'eja') return 'eja'
  return 'fund1'
}

function turnoComRefeicao(refeicao: RefeicaoFicha): TurnoReferencia {
  for (const t of TURNOS_REFERENCIA) {
    if (refeicoesDoTurno(t.value).includes(refeicao)) return t.value
  }
  return 'manha'
}

export function FichaCatalogoTab({ onError, onInfo, onAjustar, onNova }: Props) {
  const { canDelete } = useAuth()
  const canExcluir = canDelete('ficha_tecnica')
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [rows, setRows] = useState<Row[]>([])
  const [filtroMod, setFiltroMod] = useState<GrupoFiltro>('todas')
  const [filtroRef, setFiltroRef] = useState<RefeicaoFicha | 'todas'>('todas')
  const [busca, setBusca] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    onError(null)
    const [fRes, iRes, pRes, linkRes, alRes, cfgRes, precosRes, prodRes] = await Promise.all([
      supabase.from('fichas_tecnicas').select('*').order('modalidade').order('refeicao'),
      supabase.from('ficha_tecnica_itens').select('*').order('ordem'),
      supabase.from('pratos').select('id, nome, descritivo'),
      supabase.from('ficha_tecnica_pratos').select('*').order('ordem'),
      supabase
        .from('alimentos_macronutrientes')
        .select(
          'id, nome, produto_id, energia_kcal, energia_kj, proteinas_g, carboidratos_g, lipideos_g, calcio_mg, ferro_mg, vitamina_a_mcg, vitamina_c_g',
        ),
      supabase.from('configuracoes').select('nutricionista_responsavel').eq('id', 1).maybeSingle(),
      supabase.from('precos').select('id, produto_id, valor_unitario, vigencia_inicio, criado_em'),
      supabase
        .from('produtos')
        .select(
          'id, unidade, gramas_por_unidade, unidades_por_embalagem, gramas_por_item, ml_por_unidade, gramas_por_ml',
        ),
    ])
    if (fRes.error) {
      onError(fRes.error.message)
      setLoading(false)
      return
    }
    if (iRes.error) onError(iRes.error.message)
    if (pRes.error) onError(pRes.error.message)
    if (linkRes.error) onError(linkRes.error.message)
    if (alRes.error) onError(alRes.error.message)
    if (cfgRes.error) onError(cfgRes.error.message)
    if (precosRes.error) onError(precosRes.error.message)
    if (prodRes.error) onError(prodRes.error.message)

    const fichas = (fRes.data as FichaTecnica[]) || []
    const itens = (iRes.data as FichaTecnicaItem[]) || []
    const pratos = (pRes.data as Pick<Prato, 'id' | 'nome' | 'descritivo'>[]) || []
    const links = (linkRes.data as FichaTecnicaPrato[]) || []
    const alimentos = (alRes.data as AlimentoMacronutriente[]) || []
    const precos = (precosRes.data as Preco[]) || []
    const produtos = (prodRes.data as Produto[]) || []
    const nutricionista = cfgRes.data?.nutricionista_responsavel ?? ''
    const refDate = todayISO()
    const alimentosById = new Map(alimentos.map((a) => [a.id, a]))
    const pratoById = new Map(pratos.map((p) => [p.id, p]))
    const produtoById = new Map(produtos.map((p) => [p.id, p]))

    const built: Row[] = []
    for (const ficha of fichas) {
      const fichaPratos = links
        .filter((l) => l.ficha_id === ficha.id)
        .map((l) => pratoById.get(l.prato_id))
        .filter(Boolean) as Pick<Prato, 'id' | 'nome' | 'descritivo'>[]

      const slices: {
        pratoId: string | null
        nomePreparacao: string
        modoPreparo: string
      }[] =
        fichaPratos.length > 0
          ? fichaPratos.map((p) => ({
              pratoId: p.id,
              nomePreparacao: p.nome,
              modoPreparo: p.descritivo?.trim() || '',
            }))
          : [
              {
                pratoId: null,
                nomePreparacao:
                  ficha.descritivo?.trim() ||
                  `${MOD_LABEL[ficha.modalidade] || ficha.modalidade} — ${labelRefeicao(ficha.refeicao as RefeicaoFicha)}`,
                modoPreparo: ficha.descritivo?.trim() || '',
              },
            ]

      for (const slice of slices) {
        const fItens = filterFichaItensPorPrato(itens, ficha.id, slice.pratoId) as FichaTecnicaItem[]
        const itensLabel = fItens
          .map((it) => {
            const nome = alimentosById.get(it.alimento_id)?.nome || 'Alimento'
            return `${nome} (${formatNumber(Number(it.quantidade_g) || 0, 0)}g)`
          })
          .join(', ')
        const nuts = fItens.map((it) =>
          nutrientesDaPorcao(alimentosById.get(it.alimento_id), Number(it.quantidade_g) || 0),
        )
        const tot = somarNutrientes(nuts)
        const exportPayload: FichaExportPayload = {
          modalidadeLabel: MOD_LABEL[ficha.modalidade] || ficha.modalidade,
          nomePreparacao: slice.nomePreparacao,
          ingredientes: fItens.map((it, idx) => {
            const al = alimentosById.get(it.alimento_id)
            const nut = nuts[idx]
            return {
              codigo: '',
              descricao: al?.nome || 'Alimento',
              qtdG: Number(it.quantidade_g) || 0,
              energiaKcal: nut.energia_kcal,
              proteinaG: nut.proteinas_g,
              lipideosG: nut.lipideos_g,
              carboidratoG: nut.carboidratos_g,
              calcioMg: nut.calcio_mg,
              ferroMg: nut.ferro_mg,
              retinolMcg: nut.vitamina_a_mcg,
              vitaminaCMg: nut.vitamina_c_g,
            }
          }),
        }
        const insumosPayload = buildFichaInsumosPayload({
          nomePreparacao: slice.nomePreparacao,
          modoPreparo: slice.modoPreparo,
          profissional: nutricionista || '',
          itens: fItens,
          alimentosById,
          produtosById: produtoById,
          precos,
          refDate,
        })
        built.push({
          ficha,
          pratoId: slice.pratoId,
          pratoNome: slice.nomePreparacao,
          modoPreparoLabel: slice.modoPreparo || 'Sem modo de preparo',
          itensLabel,
          kcal: tot.energia_kcal,
          ptn: tot.proteinas_g,
          cho: tot.carboidratos_g,
          lip: tot.lipideos_g,
          exportPayload,
          insumosPayload,
        })
      }
    }
    setRows(built)
    setLoading(false)
  }, [onError])

  const baixarRelatorioGeral = useCallback(
    async (lista: Row[], filename: string) => {
      if (!lista.length) {
        onError('Nenhuma ficha técnica para exportar com os filtros atuais.')
        return
      }
      setExporting(true)
      onError(null)
      try {
        await downloadFichasTecnicasXlsx(
          lista.map((r) => r.exportPayload),
          filename,
        )
        onInfo(`${lista.length} fichas técnicas baixadas em Excel (relatório nutricional).`)
      } catch (e) {
        onError(e instanceof Error ? e.message : 'Falha ao gerar o Excel da ficha técnica.')
      } finally {
        setExporting(false)
      }
    },
    [onError, onInfo],
  )

  const baixarInsumosLinha = useCallback(
    async (row: Row) => {
      setExporting(true)
      onError(null)
      try {
        const safe = (row.insumosPayload.nomePreparacao || 'ficha')
          .replace(/[\\/:*?"<>|]+/g, '-')
          .trim()
          .slice(0, 60)
        await downloadFichaTecnicaInsumosPdf(
          row.insumosPayload,
          `ficha-tecnica-${safe || 'ficha'}.pdf`,
        )
        onInfo('Ficha técnica (insumos) baixada em PDF.')
      } catch (e) {
        onError(e instanceof Error ? e.message : 'Falha ao gerar o PDF da ficha técnica.')
      } finally {
        setExporting(false)
      }
    },
    [onError, onInfo],
  )

  const excluirFicha = useCallback(
    async (row: Row) => {
      if (!canExcluir) return
      const { modalidade, refeicao, id } = row.ficha
      const { count, error: countErr } = await supabase
        .from('cardapios')
        .select('id', { count: 'exact', head: true })
        .eq('modalidade', modalidade)
        .eq('refeicao', refeicao)
        .neq('descricao', '')
      if (countErr) {
        onError(countErr.message)
        return
      }
      const n = count || 0
      const msg =
        n > 0
          ? `Tem certeza de que deseja remover esta ficha técnica? Essa ação desvinculará a refeição de planejamentos associados.\n\nHá ${n} dia(s) com cardápio para esta modalidade × refeição — o cardápio desses slots será limpo.`
          : 'Tem certeza de que deseja remover esta ficha técnica? Essa ação desvinculará a refeição de planejamentos associados.'
      if (!window.confirm(msg)) return
      setDeletingId(id)
      onError(null)
      try {
        if (n > 0) {
          const { error: clearErr } = await supabase
            .from('cardapios')
            .delete()
            .eq('modalidade', modalidade)
            .eq('refeicao', refeicao)
          if (clearErr) throw new Error(clearErr.message)
        }
        const { error: delErr } = await supabase.from('fichas_tecnicas').delete().eq('id', id)
        if (delErr) throw new Error(delErr.message)
        onInfo('Ficha técnica removida.')
        await load()
      } catch (e) {
        onError(e instanceof Error ? e.message : 'Falha ao excluir ficha')
      } finally {
        setDeletingId(null)
      }
    },
    [canExcluir, load, onError, onInfo],
  )

  useEffect(() => {
    void load()
  }, [load])

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase()
    const g = GRUPOS_FILTRO.find((x) => x.id === filtroMod)
    return rows.filter((r) => {
      if (g && g.id !== 'todas' && !g.modalidades.includes(r.ficha.modalidade)) return false
      if (filtroRef !== 'todas' && r.ficha.refeicao !== filtroRef) return false
      if (q) {
        const texto =
          `${r.ficha.descritivo} ${r.pratoNome} ${r.modoPreparoLabel} ${r.itensLabel}`.toLowerCase()
        if (!texto.includes(q)) return false
      }
      return true
    })
  }, [rows, filtroMod, filtroRef, busca])

  if (loading) return <div className="loading">Carregando catálogo…</div>

  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Catálogo geral de fichas técnicas</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onNova}>
            + Nova ficha técnica
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={exporting || !filtradas.length}
            onClick={() =>
              void baixarRelatorioGeral(
                filtradas,
                `fichas-tecnicas-relatorio-${new Date().toISOString().slice(0, 10)}.xlsx`,
              )
            }
          >
            📥 Baixar Relatório Geral (Excel)
          </button>
        </div>
      </div>
      <p className="kpi-sub" style={{ marginBottom: 14 }}>
        Cada ficha é um <strong>template</strong> (modalidade × refeição). Ajustar gramaturas impacta o pedido
        mensal nos dias com cardápio gravado para aquela refeição. O cardápio de um dia específico é definido na
        aba Calendário.
      </p>

      <div className="filters" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Modalidade</label>
          <select value={filtroMod} onChange={(e) => setFiltroMod(e.target.value as GrupoFiltro)}>
            {GRUPOS_FILTRO.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Refeição</label>
          <select
            value={filtroRef}
            onChange={(e) => setFiltroRef(e.target.value as RefeicaoFicha | 'todas')}
          >
            <option value="todas">Todas as refeições</option>
            {REFEICOES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label>Buscar</label>
          <input
            type="search"
            placeholder="Ex.: Cuscuz, Arroz, Frango…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <p className="kpi-sub" style={{ marginBottom: 8 }}>
        {filtradas.length} preparação(ões)
      </p>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Modalidade</th>
              <th>Refeição</th>
              <th>Prato / Modo de preparo</th>
              <th>Alimentos (per capita)</th>
              <th className="col-num">kcal</th>
              <th className="col-num">PTN</th>
              <th className="col-num">CHO</th>
              <th className="col-num">LIP</th>
              <th className="col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtradas.map((r) => {
              const ref = r.ficha.refeicao as RefeicaoFicha
              return (
                <tr key={`${r.ficha.id}:${r.pratoId ?? 'ficha'}`}>
                  <td>{MOD_LABEL[r.ficha.modalidade] || r.ficha.modalidade}</td>
                  <td>
                    <strong>{labelRefeicao(ref)}</strong>
                  </td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.pratoNome || '—'}</div>
                    <div className="kpi-sub">{r.modoPreparoLabel}</div>
                  </td>
                  <td className="kpi-sub" style={{ maxWidth: 280 }}>
                    {r.itensLabel || '—'}
                  </td>
                  <td className="col-num">
                    <strong>{formatNumber(r.kcal, 0)}</strong>
                  </td>
                  <td className="col-num">{formatNumber(r.ptn, 1)}</td>
                  <td className="col-num">{formatNumber(r.cho, 1)}</td>
                  <td className="col-num">{formatNumber(r.lip, 1)}</td>
                  <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        onAjustar({
                          grupoId: grupoFromModalidade(r.ficha.modalidade),
                          turno: turnoComRefeicao(ref),
                          refeicao: ref,
                        })
                      }
                    >
                      Ajustar
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={exporting}
                      onClick={() => void baixarInsumosLinha(r)}
                    >
                      📥 Baixar PDF
                    </button>
                    {canExcluir && (
                      <>
                       {' '}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={deletingId === r.ficha.id}
                          onClick={() => void excluirFicha(r)}
                        >
                          {deletingId === r.ficha.id ? 'Excluindo…' : 'Excluir'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
            {!filtradas.length && (
              <tr>
                <td colSpan={9} className="kpi-sub" style={{ textAlign: 'center', padding: 24 }}>
                  Nenhuma ficha técnica encontrada com os filtros selecionados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
